import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import {
  createReportSchedule,
  fetchReportTemplates,
  fetchReportSchedules,
  updateReportScheduleStatus,
  updateReportSchedule,
  fetchReportLogs,
} from "../services/reportService"; // Import the fetchReportSchedules thunk
import type { ReportScheduleResponse, ReportTemplate } from "../types/reportTypes";
import {
  downloadReport,
  downloadOldReport,
  triggerScheduledReport,
} from "../services/reportService";
interface ReportState {
  isCreating: boolean;
  isLoadingTemplates: boolean;
  isLoadingSchedules: boolean; // Track the loading state for schedules
  error: string | null;
  createdReport: ReportScheduleResponse | null;
  templates: ReportTemplate[];
  reportSchedules: ReportScheduleResponse[] | null; // Store fetched report schedules
  isUpdating: boolean;
  isUpdatingSchedule: boolean;
  reportLogs: []; // Add this to initialState
  isLoadingLogs: boolean;
  isDownloading: boolean;
  downloadError: string | null;
  isDownloadingOldReport: boolean;
  downloadErrorOldReport: string | null;
  isTriggeringReport: boolean;
  triggerReportError: null;
  updatedReport: ReportScheduleResponse | null;
}

const initialState: ReportState = {
  isCreating: false,
  isLoadingTemplates: false,
  isLoadingSchedules: false, // Initialize with false
  error: null,
  createdReport: null,
  templates: [],
  reportSchedules: null, // Initially, no report schedules
  isUpdating: false,
isUpdatingSchedule:false,
  updatedReport: null,
  reportLogs: [], // Add this to initialState
  isLoadingLogs: false,
  isDownloading: false,
  downloadError: null,
  isDownloadingOldReport: false,
  downloadErrorOldReport: null,
  isTriggeringReport: false,
  triggerReportError: null,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearReportState(state) {
      state.isCreating = false;
      state.error = null;
      state.createdReport = null;
      // state.templates = [];
      // state.reportSchedules = null; // Clear report schedules as well
    },
  },
  extraReducers: (builder) => {
    // Handle createReportSchedule actions
    builder
      .addCase(createReportSchedule.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(
        createReportSchedule.fulfilled,
        (state, action: PayloadAction<ReportScheduleResponse>) => {
          state.isCreating = false;
          state.createdReport = action.payload;
        }
      )
      .addCase(createReportSchedule.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || "Failed to create report schedule";
      });
    // reportSlice.ts inside extraReducers

    builder
      .addCase(updateReportSchedule.pending, (state) => {
        state.isLoadingSchedules = true;
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(
        updateReportSchedule.fulfilled,
        (state, action: PayloadAction<ReportScheduleResponse>) => {
          state.isLoadingSchedules = false;
          state.isUpdating = false;
          state.updatedReport = action.payload;
          if (state.reportSchedules) {
            const index = state.reportSchedules.findIndex(
              (schedule) => schedule.id === action.payload.id
            );
            if (index !== -1) {
              state.reportSchedules[index] = action.payload;
            }
          }
        }
      )
      .addCase(updateReportSchedule.rejected, (state: any, action) => {
        state.isLoadingSchedules = false;

        state.isUpdating = false;
        state.error = action.payload || "Failed to update report schedule";
      });

    // Handle fetchReportTemplates actions
    builder
      .addCase(fetchReportTemplates.pending, (state) => {
        state.isLoadingTemplates = true;
        state.error = null;
      })
      .addCase(
        fetchReportTemplates.fulfilled,
        (state, action: PayloadAction<ReportTemplate[]>) => {
          state.isLoadingTemplates = false;
          state.templates = action.payload;
        }
      )
      .addCase(fetchReportTemplates.rejected, (state, action) => {
        state.isLoadingTemplates = false;
        state.error = action.payload || "Failed to fetch report templates";
      });

    // Handle fetchReportSchedules actions
    builder
      .addCase(fetchReportSchedules.pending, (state) => {
        state.isLoadingSchedules = true;
        state.error = null;
      })
      .addCase(
        fetchReportSchedules.fulfilled,
        (state, action: PayloadAction<ReportScheduleResponse[]>) => {
          state.isLoadingSchedules = false;
          state.reportSchedules = action.payload;
        }
      )
      .addCase(fetchReportSchedules.rejected, (state: any, action) => {
        state.isLoadingSchedules = false;
        state.error = action.payload || "Failed to fetch report schedules";
      });
    builder
      .addCase(updateReportScheduleStatus.pending, (state) => {
        state.isUpdatingSchedule = true;
      })
      .addCase(
        updateReportScheduleStatus.fulfilled,
        (state, action: PayloadAction<ReportScheduleResponse>) => {
          state.isUpdatingSchedule = false;
          if (state.reportSchedules) {
            const index = state.reportSchedules.findIndex(
              (schedule) => schedule.id === action.payload.id
            );
            if (index !== -1) {
              // Update only the 'is_active' field without changing other fields
              state.reportSchedules[index].is_active = action.payload.is_active;
            }
          }
        }
      )

      .addCase(updateReportScheduleStatus.rejected, (state: any, action) => {
      state.isUpdatingSchedule = false;
        state.error =
          action.payload || "Failed to update report schedule status";
      })
      .addCase(fetchReportLogs.pending, (state) => {
        state.isLoadingLogs = true;
      })
      .addCase(fetchReportLogs.fulfilled, (state, action) => {
        state.isLoadingLogs = false;
        state.reportLogs = action.payload;
      })
      .addCase(fetchReportLogs.rejected, (state: any, action) => {
        state.isLoadingLogs = false;
        state.error = action.payload || "Failed to fetch report logs";
      });
    builder
      .addCase(downloadReport.pending, (state) => {
        state.isDownloading = true;
        state.downloadError = null;
      })
      .addCase(downloadReport.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(downloadReport.rejected, (state, action) => {
        state.isDownloading = false;
        state.downloadError = action.payload || "Failed to download report";
      });
    builder
      .addCase(downloadOldReport.pending, (state) => {
        state.isDownloadingOldReport = true;
        state.downloadErrorOldReport = null;
      })
      .addCase(downloadOldReport.fulfilled, (state) => {
        state.isDownloadingOldReport = false;
      })
      .addCase(downloadOldReport.rejected, (state, action) => {
        state.isDownloadingOldReport = false;
        state.downloadErrorOldReport =
          action.payload || "Failed to download report";
      });
    builder
      .addCase(triggerScheduledReport.pending, (state: any) => {
        state.isTriggeringReport = true;
        state.triggerReportError = null;
      })
      .addCase(triggerScheduledReport.fulfilled, (state: any) => {
        state.isTriggeringReport = false;
      })
      .addCase(triggerScheduledReport.rejected, (state: any, action: any) => {
        state.isTriggeringReport = false;
        state.triggerReportError =
          action.payload || "Failed to trigger report.";
      });
  },
});

export const { clearReportState } = reportSlice.actions;
export default reportSlice.reducer;
